import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useRefreshListener } from '../hooks/useRefreshListener';
import { validateNIF, validatePhone } from '../utils/ptValidators';
import { toast } from 'sonner';
import { LeadDataDialog } from '../components/LeadDataDialog';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { BulkAssignDialog } from '../components/BulkAssignDialog';
import { DoubleScroll } from '../components/DoubleScroll';
import { HelpButton } from '../components/HelpButton';
import { LEAD_STATUS_CONFIG, LEAD_SECTOR_CONFIG, leadSector } from '../lib/leadStatus';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Phone,
  Mail,
  MapPin,
  Search,
  Loader2,
  Copy,
  Check,
  ExternalLink,
  Filter,
  RefreshCw,
  Eye,
  Edit3,
  Link2,
  UserPlus,
  ArrowRight,
  UserCog,
  UserX,
  Zap,
  Tv,
  Shield,
  Sun,
  BatteryCharging,
  Flame,
  Plus,
  Trash2,
  AlertTriangle,
  FileEdit
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { ClientLink } from '../components/client360/c360Shared';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Email format regex (mirrors backend _EMAIL_RE)
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const validateEmailFmt = (v) => !v || EMAIL_RE.test(v.trim());
const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

// Categories and companies for lead conversion
const CONVERSION_CATEGORIES = [
  {
    key: 'energias',
    label: 'Luz e Gas',
    icon: Flame,
    iconColor: 'text-orange-500',
    bgColor: 'bg-orange-50',
    companies: [
      { key: 'edp', name: 'EDP' },
      { key: 'galp', name: 'Galp' },
      { key: 'endesa', name: 'Endesa' },
      { key: 'iberdrola', name: 'Iberdrola' },
      { key: 'gold', name: 'Gold Energy' },
      { key: 'repsol', name: 'Repsol' },
      { key: 'yes', name: 'Yes' },
    ]
  },
  {
    key: 'solar',
    label: 'Solar',
    icon: Sun,
    iconColor: 'text-amber-500',
    bgColor: 'bg-amber-50',
    companies: [
      { key: 'edp', name: 'EDP' },
      { key: 'galp', name: 'Galp' },
      { key: 'iberdrola', name: 'Iberdrola' },
    ]
  },
  {
    key: 'carregamento',
    label: 'Carregamento Eletrico',
    icon: BatteryCharging,
    iconColor: 'text-green-500',
    bgColor: 'bg-green-50',
    companies: [
      { key: 'edp', name: 'EDP' },
      { key: 'galp', name: 'Galp' },
      { key: 'iberdrola', name: 'Iberdrola' },
    ]
  },
  {
    key: 'telecomunicacoes',
    label: 'Telecomunicacoes',
    icon: Tv,
    iconColor: 'text-purple-500',
    bgColor: 'bg-purple-50',
    companies: [
      { key: 'nos', name: 'NOS' },
      { key: 'meo', name: 'MEO' },
      { key: 'vodafone', name: 'Vodafone' },
    ]
  },
  {
    key: 'alarmes',
    label: 'Alarmes',
    icon: Shield,
    iconColor: 'text-red-500',
    bgColor: 'bg-red-50',
    companies: [
      { key: 'nos_securitas', name: 'NOS Securitas' },
      { key: 'prosegur', name: 'Prosegur' },
    ]
  },
];

const STATUS_CONFIG = LEAD_STATUS_CONFIG;

const LeadStatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.nova;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`} data-testid={`lead-status-${status}`}>
      {config.label}
    </span>
  );
};

export const LeadsPage = () => {
  const { user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [bulkSectorValue, setBulkSectorValue] = useState('');
  const [settingSector, setSettingSector] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const canBulkDelete = ['super_admin', 'gestor'].includes(user?.role);
  const canAssign = ['super_admin', 'gestor', 'admin'].includes(user?.role);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteLeadConfirm, setDeleteLeadConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Conversion modal state
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [convertStep, setConvertStep] = useState('category'); // 'category' or 'company'
  const [selectedCategory, setSelectedCategory] = useState(null);

  // New lead modal
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [newLeadSubmitting, setNewLeadSubmitting] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    client_name: '', client_phone: '', client_email: '', client_nif: '',
    client_address: '', client_locality: '', service_interest: '', notes: '',
    next_action_at: '',
    partner_id: '', assigned_to: '',
    source: '',
  });

  // Lead sources (builtin + custom from backend)
  const [leadSourceLabels, setLeadSourceLabels] = useState({});
  const [newSourceDialogOpen, setNewSourceDialogOpen] = useState(false);
  const [newSourceLabelInput, setNewSourceLabelInput] = useState('');
  const [creatingSource, setCreatingSource] = useState(false);
  const canManageSources = ['super_admin', 'gestor', 'admin'].includes(user?.role);

  // Edit lead modal
  const [editLeadOpen, setEditLeadOpen] = useState(false);
  const [editLeadForm, setEditLeadForm] = useState(null);
  const [editLeadSaving, setEditLeadSaving] = useState(false);

  // Assignable partners / users (only for super_admin / gestor / admin / parceiro / backoffice / chefe_equipa)
  const [assignablePartners, setAssignablePartners] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const isAngariador = user?.role === 'angariador';
  const canManage = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'comercial'].includes(user?.role);
  const canSubmitLead = isAngariador || canManage;
  // Roles autorizados a escolher Parceiro / Comercial ao criar lead manualmente
  const canAssignToOthers = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'chefe_equipa'].includes(user?.role);
  // Roles que veem coluna "Atribuição" completa (Comercial + Parceiro + Angariador)
  const canSeeAttribution = ['super_admin', 'gestor', 'admin', 'parceiro', 'backoffice', 'chefe_equipa'].includes(user?.role);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/leads`);
      setLeads(res.data);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);
  useRefreshListener(fetchLeads);

  // Carregar parceiros + utilizadores atribuíveis (só para roles privilegiados)
  useEffect(() => {
    if (!canAssignToOthers) return;
    (async () => {
      try {
        const [pRes, uRes] = await Promise.all([
          axios.get(`${API_URL}/leads/assignable-partners`),
          axios.get(`${API_URL}/leads/assignable-users`),
        ]);
        setAssignablePartners(pRes.data || []);
        setAssignableUsers(uRes.data || []);
      } catch (e) {
        console.error('Failed to load assignable partners/users', e);
      }
    })();
  }, [canAssignToOthers]);

  // Carregar origens (builtin + custom)
  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/leads/config/sources`)
      .then(r => { if (alive && r.data) setLeadSourceLabels(r.data); })
      .catch(err => console.error('Failed to load lead sources', err));
    return () => { alive = false; };
  }, []);

  const reloadSources = async () => {
    try {
      const r = await axios.get(`${API_URL}/leads/config/sources`);
      if (r.data) setLeadSourceLabels(r.data);
    } catch { /* ignore */ }
  };

  const handleCreateNewSource = async () => {
    const label = newSourceLabelInput.trim();
    if (!label) { toast.error('Insere um nome'); return; }
    setCreatingSource(true);
    try {
      const r = await axios.post(`${API_URL}/leads/config/sources`, { label });
      toast.success(`Origem "${label}" criada`);
      await reloadSources();
      if (editLeadOpen) {
        setEditLeadForm(p => p ? { ...p, source: r.data.key } : p);
      } else {
        setNewLeadForm(p => ({ ...p, source: r.data.key }));
      }
      setNewSourceDialogOpen(false);
      setNewSourceLabelInput('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao criar origem');
    } finally {
      setCreatingSource(false);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = searchQuery === '' || 
      (lead.client_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (lead.client_phone || '').includes(searchQuery) ||
      (lead.angariador_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (lead.client_email || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || lead.source === sourceFilter;
    
    return matchesSearch && matchesStatus && matchesSource;
  });

  // Uma lead atribuída a um gestor conta como POR ATRIBUIR (gestor não é comercial)
  const needsAssignment = (l) => !l.assigned_to || l.assigned_is_manager;
  // Bulk selection helpers (selectable = leads por atribuir e não convertidas)
  const selectableLeads = filteredLeads.filter((l) => l.status !== 'convertida' && needsAssignment(l));
  const allSelected = selectableLeads.length > 0 && selectableLeads.every((l) => selectedIds.has(l.id));
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelectedIds((prev) => {
    if (selectableLeads.every((l) => prev.has(l.id))) return new Set();
    return new Set(selectableLeads.map((l) => l.id));
  });
  const clearSelection = () => setSelectedIds(new Set());

  // ===== Classificar Setor em massa (leads sem setor) =====
  const SECTOR_OPTIONS = ["Luz e Gas", "Solar", "Carregamento Eletrico", "Telecomunicacoes", "Alarmes", "Outro"];
  const leadsWithoutSector = leads.filter((l) => !l.service_interest);
  const applyBulkSector = async (ids, value) => {
    if (!value) { toast.error('Escolhe um setor'); return; }
    if (!ids || ids.length === 0) { toast.info('Não há leads para classificar'); return; }
    setSettingSector(true);
    try {
      const r = await axios.post(`${API_URL}/leads/bulk-set-sector`, { lead_ids: ids, service_interest: value });
      toast.success(r.data?.message || 'Setor aplicado');
      await fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao classificar setor');
    } finally {
      setSettingSector(false);
    }
  };
  const setOneSector = async (leadId, value) => {
    if (!value) return;
    try {
      await axios.post(`${API_URL}/leads/bulk-set-sector`, { lead_ids: [leadId], service_interest: value });
      await fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao classificar setor');
    }
  };

  // Atribuição em massa OU por linha — alvo do diálogo
  const [dialogLeadIds, setDialogLeadIds] = useState([]);
  const openBulkAssign = () => { setDialogLeadIds(Array.from(selectedIds)); setBulkOpen(true); };

  const doBulkDelete = async () => {
    setDeletingBulk(true);
    try {
      const res = await axios.post(`${API_URL}/leads/bulk-delete`, { lead_ids: Array.from(selectedIds) });
      toast.success(res.data?.message || 'Leads apagadas');
      setBulkDeleteOpen(false);
      clearSelection();
      await fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao apagar leads');
    } finally {
      setDeletingBulk(false);
    }
  };
  const openReassign = (lead) => { setDialogLeadIds([lead.id]); setBulkOpen(true); };

  // Pôr uma lead já atribuída de volta por atribuir (ação por linha)
  const [unassigningId, setUnassigningId] = useState(null);
  const unassignLead = async (lead) => {
    setUnassigningId(lead.id);
    try {
      const res = await axios.post(`${API_URL}/leads/bulk-assign`, { lead_ids: [lead.id], mode: 'unassign' });
      toast.success(res.data?.message || 'Lead posta por atribuir');
      fetchLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao pôr por atribuir');
    } finally {
      setUnassigningId(null);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedLead || !newStatus) return;
    if (newStatus === 'incontactavel' && !statusNotes.trim()) { toast.error('Indique uma nota (ex.: liguei 3x, não atende)'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/leads/${selectedLead.id}/status`, {
        status: newStatus,
        notes: statusNotes,
      });
      await fetchLeads();
      setEditStatusOpen(false);
      setSelectedLead(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao mudar estado');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!deleteLeadConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/leads/${deleteLeadConfirm.id}`);
      setDeleteLeadConfirm(null);
      await fetchLeads();
    } catch (err) {
      console.error('Error deleting lead:', err);
      alert(err?.response?.data?.detail || 'Erro ao eliminar lead');
    } finally {
      setDeleting(false);
    }
  };

  const openDetail = (lead) => {
    setSelectedLead(lead);
    setDetailOpen(true);
  };

  const openEditStatus = (lead) => {
    setSelectedLead(lead);
    setNewStatus(lead.status);
    setStatusNotes('');
    setEditStatusOpen(true);
  };

  const copyReferralLink = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(`${APP_ORIGIN}/lead/${user.referral_code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmitNewLead = async () => {
    if (!newLeadForm.client_name || !newLeadForm.client_phone) return;
    // Strict PT validation
    const nifCheck = validateNIF(newLeadForm.client_nif);
    if (!nifCheck.ok) { toast.error(`NIF: ${nifCheck.error}`); return; }
    const phCheck = validatePhone(newLeadForm.client_phone, newLeadForm.client_phone_tipo || 'telemovel');
    if (!phCheck.ok) { toast.error(`Telefone: ${phCheck.error}`); return; }
    if (newLeadForm.client_email && !validateEmailFmt(newLeadForm.client_email)) {
      toast.error('Email inválido. Usa o formato nome@dominio.xx');
      return;
    }
    if (!newLeadForm.service_interest) { toast.error('Serviço de interesse é obrigatório (setor).'); return; }
    setNewLeadSubmitting(true);
    try {
      const payload = { ...newLeadForm };
      // partner_id é só auxiliar para filtrar no UI — não enviamos ao backend
      delete payload.partner_id;
      if (!payload.assigned_to) delete payload.assigned_to;
      if (payload.next_action_at) {
        try { payload.next_action_at = new Date(payload.next_action_at).toISOString(); } catch { /* keep original value */ }
      } else {
        delete payload.next_action_at;
      }
      await axios.post(`${API_URL}/leads/submit`, payload);
      await fetchLeads();
      setNewLeadOpen(false);
      setNewLeadForm({ client_name: '', client_phone: '', client_email: '', client_nif: '', client_address: '', client_locality: '', service_interest: '', notes: '', next_action_at: '', partner_id: '', assigned_to: '', source: '' });
      toast.success('Lead criada com sucesso');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao criar lead');
    } finally {
      setNewLeadSubmitting(false);
    }
  };

  // === Edit Lead ===
  const [dataLeadId, setDataLeadId] = useState(null);
  const openEditLead = (lead) => {
    setEditLeadForm({
      id: lead.id,
      client_name: lead.client_name || '',
      client_phone: lead.client_phone || '',
      client_phone_tipo: lead.client_phone_tipo || 'telemovel',
      client_email: lead.client_email || '',
      client_nif: lead.client_nif || '',
      client_address: lead.client_address || '',
      client_locality: lead.client_locality || '',
      service_interest: lead.service_interest || '',
      notes: lead.notes || '',
      source: lead.source || '',
    });
    setEditLeadOpen(true);
  };

  const handleSaveEditLead = async () => {
    if (!editLeadForm) return;
    if (!editLeadForm.client_name?.trim()) { toast.error('Nome obrigatório'); return; }
    if (!editLeadForm.client_phone?.trim()) { toast.error('Telefone obrigatório'); return; }
    const phCheck = validatePhone(editLeadForm.client_phone, editLeadForm.client_phone_tipo || 'telemovel');
    if (!phCheck.ok) { toast.error(`Telefone: ${phCheck.error}`); return; }
    if (editLeadForm.client_nif) {
      const nifCheck = validateNIF(editLeadForm.client_nif);
      if (!nifCheck.ok) { toast.error(`NIF: ${nifCheck.error}`); return; }
    }
    if (editLeadForm.client_email && !validateEmailFmt(editLeadForm.client_email)) {
      toast.error('Email inválido. Usa o formato nome@dominio.xx');
      return;
    }
    setEditLeadSaving(true);
    try {
      await axios.put(`${API_URL}/leads/${editLeadForm.id}`, {
        client_name: editLeadForm.client_name,
        client_phone: editLeadForm.client_phone,
        client_phone_tipo: editLeadForm.client_phone_tipo,
        client_email: editLeadForm.client_email,
        client_nif: editLeadForm.client_nif,
        client_address: editLeadForm.client_address,
        client_locality: editLeadForm.client_locality,
        service_interest: editLeadForm.service_interest,
        notes: editLeadForm.notes,
        source: editLeadForm.source,
      });
      toast.success('Lead atualizada');
      setEditLeadOpen(false);
      setEditLeadForm(null);
      await fetchLeads();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao atualizar lead');
    } finally {
      setEditLeadSaving(false);
    }
  };

  const openConvertModal = (lead) => {
    setConvertLead(lead);
    setConvertStep('category');
    setSelectedCategory(null);
    setConvertOpen(true);
  };

  const handleSelectCategory = (cat) => {
    setSelectedCategory(cat);
    setConvertStep('company');
  };

  const handleSelectCompany = (company) => {
    if (!convertLead || !selectedCategory) return;
    const params = new URLSearchParams({
      from_lead: 'true',
      lead_id: convertLead.id,
      cliente_nome: convertLead.client_name || '',
      cliente_telefone: convertLead.client_phone || '',
      cliente_email: convertLead.client_email || '',
      cliente_nif: convertLead.client_nif || '',
      cliente_morada: convertLead.client_address || '',
      cliente_localidade: convertLead.client_locality || '',
    });
    // Smart-fill: se a lead vier de "Meu Cliente" com comercializadora anterior preenchida,
    // pré-popular o campo "Comercializadora Anterior" do contrato (slug)
    if (convertLead.entrada_direta) {
      params.set('comercializadora_anterior', 'primeira_contratacao');
    } else if (convertLead.source === 'meu_cliente' && convertLead.previous_commercializer_name) {
      params.set('comercializadora_anterior', convertLead.previous_commercializer_name.toLowerCase().replace(/\s+/g, '_'));
    }
    setConvertOpen(false);
    navigate(`/contracts/${selectedCategory.key}/${company.key}/new?${params.toString()}`);
  };

  const stats = {
    total: leads.length,
    novas: leads.filter(l => l.status === 'nova').length,
    em_contacto: leads.filter(l => l.status === 'em_contacto').length,
    convertidas: leads.filter(l => l.status === 'convertida').length,
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return format(parseISO(d), "dd/MM/yy HH:mm", { locale: pt }); } catch { return d; }
  };

  return (
    <Layout>
      <LeadDataDialog leadId={dataLeadId} open={!!dataLeadId} onClose={() => setDataLeadId(null)} onSaved={fetchLeads} />
      <div className="space-y-6" data-testid="leads-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="leads-page-title">
              {isAngariador ? 'As Minhas Leads' : 'Gestao de Leads'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {isAngariador 
                ? 'Acompanhe o estado das leads que submeteu' 
                : 'Leads submetidas por angariadores externos'}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <HelpButton category="leads" />
            {canSubmitLead && hasPerm('criar_leads') && (
              <Button 
                onClick={() => setNewLeadOpen(true)}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2"
                data-testid="new-lead-btn"
              >
                <Plus className="w-4 h-4" />
                Nova Lead
              </Button>
            )}
            {user?.referral_code && !isAngariador && (
              <Button 
                variant="outline" 
                onClick={copyReferralLink}
                className="gap-2"
                data-testid="copy-referral-link-btn"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Link2 className="w-4 h-4" />}
                {copied ? 'Copiado!' : 'Copiar Link'}
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={fetchLeads}
              data-testid="refresh-leads-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats (cartões clicáveis = filtram por estado) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button
            type="button"
            onClick={() => setStatusFilter('all')}
            className={`text-left rounded-xl border transition-all ${statusFilter === 'all' ? 'border-slate-400 ring-2 ring-slate-300 shadow-md' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'} bg-slate-50`}
            data-testid="stat-total"
          >
            <div className="p-4">
              <div className="text-2xl font-bold text-slate-800">{stats.total}</div>
              <div className="text-sm text-slate-600">Total</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === 'nova' ? 'all' : 'nova')}
            className={`text-left rounded-xl border transition-all ${statusFilter === 'nova' ? 'border-blue-400 ring-2 ring-blue-200 shadow-md' : 'border-blue-100 hover:border-blue-300 hover:shadow-sm'} bg-blue-50`}
            data-testid="stat-novas"
          >
            <div className="p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.novas}</div>
              <div className="text-sm text-blue-700/80">Novas</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === 'em_contacto' ? 'all' : 'em_contacto')}
            className={`text-left rounded-xl border transition-all ${statusFilter === 'em_contacto' ? 'border-amber-400 ring-2 ring-amber-200 shadow-md' : 'border-amber-100 hover:border-amber-300 hover:shadow-sm'} bg-amber-50`}
            data-testid="stat-em-contacto"
          >
            <div className="p-4">
              <div className="text-2xl font-bold text-amber-600">{stats.em_contacto}</div>
              <div className="text-sm text-amber-700/80">Em Contacto</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter(statusFilter === 'convertida' ? 'all' : 'convertida')}
            className={`text-left rounded-xl border transition-all ${statusFilter === 'convertida' ? 'border-emerald-400 ring-2 ring-emerald-200 shadow-md' : 'border-emerald-100 hover:border-emerald-300 hover:shadow-sm'} bg-emerald-50`}
            data-testid="stat-convertidas"
          >
            <div className="p-4">
              <div className="text-2xl font-bold text-emerald-600">{stats.convertidas}</div>
              <div className="text-sm text-emerald-700/80">Convertidas</div>
            </div>
          </button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Procurar por nome, telefone, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="lead-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="lead-status-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Estados</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="lead-source-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Origem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Origens</SelectItem>
                  {Object.entries(leadSourceLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Aviso/Botão: Classificar setor em massa */}
        {canAssign && leadsWithoutSector.length > 0 && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5" data-testid="sector-classify-banner">
            <span className="text-sm text-amber-800"><strong>{leadsWithoutSector.length}</strong> lead(s) sem setor definido — não aparecem com selo no Kanban.</span>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-8" onClick={() => { setBulkSectorValue(''); setSectorDialogOpen(true); }} data-testid="open-sector-dialog-btn">Classificar Setor</Button>
          </div>
        )}

        {/* Bulk assign toolbar */}
        {canAssign && selectedIds.size > 0 && (
          <div className="flex items-center justify-between bg-[#2D3E6F] text-white rounded-xl px-4 py-2.5" data-testid="bulk-assign-toolbar">
            <span className="text-sm font-medium">{selectedIds.size} lead(s) selecionada(s)</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/15 h-8" onClick={clearSelection} data-testid="bulk-clear-btn">Limpar</Button>
              <Button size="sm" className="bg-white text-[#2D3E6F] hover:bg-slate-100 h-8" onClick={openBulkAssign} data-testid="bulk-assign-open-btn">Atribuir Comercial</Button>
              {canBulkDelete && (
                <Button size="sm" className="bg-rose-600 text-white hover:bg-rose-700 h-8" onClick={() => setBulkDeleteOpen(true)} data-testid="bulk-delete-open-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Apagar selecionadas
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        <Card data-testid="leads-table-scroll">
          <DoubleScroll>
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  {canAssign && (
                    <TableHead className="w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-[#2D3E6F] align-middle" data-testid="bulk-select-all" />
                    </TableHead>
                  )}
                  <TableHead className="min-w-[160px]">Cliente</TableHead>
                  <TableHead className="min-w-[130px]">Origem</TableHead>
                  <TableHead className="min-w-[120px]">Telefone</TableHead>
                  <TableHead className="min-w-[120px]">Servico</TableHead>
                  {!isAngariador && (
                    <TableHead className="min-w-[180px]">
                      {canSeeAttribution ? 'Atribuição' : 'Angariador'}
                    </TableHead>
                  )}
                  <TableHead className="min-w-[100px]">Estado</TableHead>
                  <TableHead className="min-w-[120px]">Data</TableHead>
                  <TableHead className="min-w-[80px] text-right">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={(isAngariador ? 7 : 8) + (canAssign ? 1 : 0)} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={(isAngariador ? 7 : 8) + (canAssign ? 1 : 0)} className="text-center py-12 text-slate-400">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      Nenhuma lead encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className={`hover:bg-slate-50/50 ${lead.status === 'convertida' ? 'bg-emerald-50/40 hover:bg-emerald-50/60' : ''}`}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      {canAssign && (
                        <TableCell className="w-10">
                          {lead.status !== 'convertida' && needsAssignment(lead) ? (
                            <input type="checkbox" checked={selectedIds.has(lead.id)} onChange={() => toggleSelect(lead.id)} className="w-4 h-4 accent-[#2D3E6F] align-middle" data-testid={`lead-select-${lead.id}`} />
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="font-medium text-slate-800 inline-flex items-center gap-1">{lead.client_name}<ClientLink nif={lead.client_nif} name={lead.client_name} leadId={lead.id} testId={`lead-client-link-${lead.id}`} /></div>
                        {lead.client_email && <div className="text-xs text-slate-400">{lead.client_email}</div>}
                      </TableCell>
                      <TableCell>
                        {lead.source ? (
                          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 font-normal" data-testid={`lead-source-${lead.id}`}>
                            {leadSourceLabels[lead.source] || lead.source}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Phone className="w-3 h-3 text-slate-400" />
                          {lead.client_phone}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{lead.service_interest || '-'}</TableCell>
                      {!isAngariador && (
                        <TableCell>
                          {canSeeAttribution ? (
                            <div className="text-xs space-y-0.5" data-testid={`lead-attribution-${lead.id}`}>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-slate-400 w-[68px] shrink-0">Comercial:</span>
                                {lead.assigned_is_manager || !lead.assigned_to ? (
                                  <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Por atribuir</span>
                                ) : (
                                  <span className="text-slate-700 font-medium truncate">{lead.assigned_to_name || '—'}</span>
                                )}
                              </div>
                              <div className="flex items-baseline gap-1.5">
                                <span className="text-slate-400 w-[68px] shrink-0">Parceiro:</span>
                                <span className="text-slate-600 truncate">{lead.partner_name || '—'}</span>
                              </div>
                              {lead.angariador_name && (
                                <div className="flex items-baseline gap-1.5">
                                  <span className="text-slate-400 w-[68px] shrink-0">Angariador:</span>
                                  <span className="text-emerald-700 truncate">{lead.angariador_name}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-sm">
                              <div className="font-medium text-slate-700">{lead.angariador_name || '-'}</div>
                              {lead.angariador_email && <div className="text-xs text-slate-400">{lead.angariador_email}</div>}
                            </div>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <LeadStatusBadge status={lead.status} />
                        {lead.status === 'convertida' && lead.converted_companies?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`lead-converted-companies-${lead.id}`}>
                            {lead.converted_companies.map((co) => (
                              <span key={co} className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 capitalize">
                                {co}
                              </span>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatDate(lead.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openDetail(lead)}
                            data-testid={`view-lead-${lead.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canManage && hasPerm('editar_leads') && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => (['comercial', 'chefe_equipa'].includes(user?.role) ? setDataLeadId(lead.id) : openEditLead(lead))}
                                data-testid={`edit-lead-full-${lead.id}`}
                                title="Editar dados da Lead"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <FileEdit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditStatus(lead)}
                                data-testid={`edit-lead-${lead.id}`}
                                title="Alterar Estado"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => openConvertModal(lead)}
                                data-testid={`convert-lead-${lead.id}`}
                                title="Converter em Contrato"
                              >
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canAssign && lead.assigned_to && !lead.assigned_is_manager && lead.status !== 'convertida' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                onClick={() => openReassign(lead)}
                                data-testid={`reassign-lead-${lead.id}`}
                                title="Reatribuir comercial"
                              >
                                <UserCog className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => unassignLead(lead)}
                                disabled={unassigningId === lead.id}
                                data-testid={`unassign-lead-${lead.id}`}
                                title="Pôr por atribuir"
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {['super_admin', 'gestor', 'admin'].includes(user?.role) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteLeadConfirm(lead)}
                              data-testid={`delete-lead-${lead.id}`}
                              title="Eliminar Lead"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </DoubleScroll>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg" data-testid="lead-detail-dialog">
            <DialogHeader>
              <DialogTitle>Detalhes da Lead</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-slate-500 block">Cliente</span>
                    <span className="font-medium">{selectedLead.client_name}</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">Estado</span>
                    <LeadStatusBadge status={selectedLead.status} />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">Telefone</span>
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selectedLead.client_phone}</span>
                  </div>
                  {selectedLead.client_email && (
                    <div>
                      <span className="text-xs text-slate-500 block">Email</span>
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selectedLead.client_email}</span>
                    </div>
                  )}
                  {selectedLead.client_nif && (
                    <div>
                      <span className="text-xs text-slate-500 block">NIF</span>
                      <span>{selectedLead.client_nif}</span>
                    </div>
                  )}
                  {selectedLead.client_locality && (
                    <div>
                      <span className="text-xs text-slate-500 block">Localidade</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{selectedLead.client_locality}</span>
                    </div>
                  )}
                  {selectedLead.client_address && (
                    <div className="col-span-2">
                      <span className="text-xs text-slate-500 block">Morada</span>
                      <span>{selectedLead.client_address}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-xs text-slate-500 block">Servico</span>
                    <span>{selectedLead.service_interest || '-'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 block">Data</span>
                    <span>{formatDate(selectedLead.created_at)}</span>
                  </div>
                </div>
                
                {!isAngariador && (
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Atribuição</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-slate-500 block">Comercial Atribuído</span>
                        <span className="font-medium">{selectedLead.assigned_to_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 block">Parceiro</span>
                        <span>{selectedLead.partner_name || '-'}</span>
                      </div>
                      {selectedLead.angariador_name && (
                        <>
                          <div>
                            <span className="text-xs text-slate-500 block">Angariador</span>
                            <span className="text-emerald-700">{selectedLead.angariador_name}</span>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500 block">Email Angariador</span>
                            <span>{selectedLead.angariador_email || '-'}</span>
                          </div>
                          {selectedLead.angariador_partner_name && (
                            <div>
                              <span className="text-xs text-slate-500 block">Parceiro do Angariador</span>
                              <span>{selectedLead.angariador_partner_name}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedLead.notes && (
                  <div className="border-t pt-4">
                    <span className="text-xs text-slate-500 block mb-1">Observacoes</span>
                    <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedLead.notes}</p>
                  </div>
                )}
                
                {selectedLead.admin_notes && (
                  <div className="border-t pt-4">
                    <span className="text-xs text-slate-500 block mb-1">Notas internas</span>
                    <p className="text-sm text-slate-700 bg-amber-50 p-3 rounded-lg">{selectedLead.admin_notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Status Dialog */}
        <Dialog open={editStatusOpen} onOpenChange={setEditStatusOpen}>
          <DialogContent data-testid="edit-lead-status-dialog">
            <DialogHeader>
              <DialogTitle>Alterar Estado da Lead</DialogTitle>
            </DialogHeader>
            {selectedLead && (
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-slate-500">Cliente: <strong>{selectedLead.client_name}</strong></span>
                </div>
                <div>
                  <span className="text-sm text-slate-600 block mb-1">Novo Estado</span>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="new-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <span className="text-sm text-slate-600 block mb-1">Notas</span>
                  <Textarea 
                    value={statusNotes}
                    onChange={(e) => setStatusNotes(e.target.value)}
                    placeholder="Notas sobre a alteracao..."
                    rows={3}
                    data-testid="status-notes-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditStatusOpen(false)}>Cancelar</Button>
              <Button 
                onClick={handleUpdateStatus}
                disabled={saving}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                data-testid="save-lead-status-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert Lead to Contract Dialog */}
        <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
          <DialogContent className="max-w-md" data-testid="convert-lead-dialog">
            <DialogHeader>
              <DialogTitle>
                {convertStep === 'category' ? 'Converter Lead em Contrato' : `Escolher Empresa`}
              </DialogTitle>
            </DialogHeader>
            {convertLead && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <span className="text-slate-500">Cliente:</span>{' '}
                  <strong className="text-slate-800">{convertLead.client_name}</strong>
                  {convertLead.service_interest && (
                    <span className="text-slate-400 ml-2">({convertLead.service_interest})</span>
                  )}
                </div>

                {convertStep === 'category' && (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500">Selecione o servico a contratar:</p>
                    <div className="grid gap-2">
                      {CONVERSION_CATEGORIES.map((cat) => {
                        const IconComp = cat.icon;
                        return (
                          <button
                            key={cat.key}
                            onClick={() => handleSelectCategory(cat)}
                            className={`flex items-center gap-3 p-3.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left ${cat.bgColor}`}
                            data-testid={`convert-cat-${cat.key}`}
                          >
                            <div className={`w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm`}>
                              <IconComp className={`w-5 h-5 ${cat.iconColor}`} />
                            </div>
                            <span className="font-medium text-slate-700">{cat.label}</span>
                            <ArrowRight className="w-4 h-4 text-slate-400 ml-auto" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {convertStep === 'company' && selectedCategory && (
                  <div className="space-y-2">
                    <button 
                      onClick={() => setConvertStep('category')} 
                      className="text-sm text-[#2D3E6F] hover:underline flex items-center gap-1"
                    >
                      ← Voltar
                    </button>
                    <p className="text-sm text-slate-500">
                      Escolha a empresa para <strong>{selectedCategory.label}</strong>:
                    </p>
                    <div className="grid gap-2">
                      {selectedCategory.companies.map((comp) => (
                        <button
                          key={comp.key}
                          onClick={() => handleSelectCompany(comp)}
                          className="flex items-center gap-3 p-3.5 rounded-lg border border-slate-200 hover:border-[#2D3E6F] hover:bg-blue-50/50 hover:shadow-sm transition-all text-left"
                          data-testid={`convert-company-${comp.key}`}
                        >
                          <span className="font-medium text-slate-700">{comp.name}</span>
                          <ArrowRight className="w-4 h-4 text-slate-400 ml-auto" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* New Lead Dialog (for logged-in angariadores/comerciais) */}
        <Dialog open={newLeadOpen} onOpenChange={setNewLeadOpen}>
          <DialogContent className="max-w-lg" data-testid="new-lead-dialog">
            <DialogHeader>
              <DialogTitle>Nova Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Nome do Cliente *</Label>
                  <Input
                    value={newLeadForm.client_name}
                    onChange={(e) => setNewLeadForm(p => ({...p, client_name: e.target.value}))}
                    placeholder="Nome completo"
                    data-testid="new-lead-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">Telefone *</Label>
                  <div className="flex gap-2">
                    <Select
                      value={newLeadForm.client_phone_tipo || 'telemovel'}
                      onValueChange={(v) => setNewLeadForm(p => ({...p, client_phone_tipo: v}))}
                    >
                      <SelectTrigger className="w-28" data-testid="new-lead-phone-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="telemovel">Telemóvel</SelectItem>
                        <SelectItem value="fixo">Fixo</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      value={newLeadForm.client_phone}
                      onChange={(e) => setNewLeadForm(p => ({...p, client_phone: e.target.value}))}
                      placeholder={(newLeadForm.client_phone_tipo === 'fixo') ? '211234567' : '912345678'}
                      data-testid="new-lead-phone"
                      maxLength={9}
                    />
                  </div>
                  {newLeadForm.client_phone && !validatePhone(newLeadForm.client_phone, newLeadForm.client_phone_tipo || 'telemovel').ok && (
                    <p className="text-xs text-rose-600 mt-1" data-testid="new-lead-phone-error">
                      {validatePhone(newLeadForm.client_phone, newLeadForm.client_phone_tipo || 'telemovel').error}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-600">Email</Label>
                  <Input
                    type="email"
                    value={newLeadForm.client_email}
                    onChange={(e) => setNewLeadForm(p => ({...p, client_email: e.target.value}))}
                    placeholder="cliente@email.com"
                    data-testid="new-lead-email"
                  />
                  {newLeadForm.client_email && !validateEmailFmt(newLeadForm.client_email) && (
                    <p className="text-xs text-rose-600 mt-1" data-testid="new-lead-email-error">
                      Email inválido. Usa o formato nome@dominio.xx
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-600">NIF</Label>
                  <Input
                    value={newLeadForm.client_nif}
                    onChange={(e) => setNewLeadForm(p => ({...p, client_nif: e.target.value}))}
                    placeholder="123456789"
                    maxLength={9}
                    data-testid="new-lead-nif"
                  />
                  {newLeadForm.client_nif && !validateNIF(newLeadForm.client_nif).ok && (
                    <p className="text-xs text-rose-600 mt-1" data-testid="new-lead-nif-error">
                      {validateNIF(newLeadForm.client_nif).error}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-600">Localidade</Label>
                  <Input
                    value={newLeadForm.client_locality}
                    onChange={(e) => setNewLeadForm(p => ({...p, client_locality: e.target.value}))}
                    placeholder="Lisboa, Porto..."
                    data-testid="new-lead-locality"
                  />
                </div>
                <div>
                  <Label className="text-slate-600">Servico Pretendido <span className="text-rose-500">*</span></Label>
                  <Select
                    value={newLeadForm.service_interest}
                    onValueChange={(v) => setNewLeadForm(p => ({...p, service_interest: v}))}
                  >
                    <SelectTrigger data-testid="new-lead-service" className={!newLeadForm.service_interest ? 'border-rose-300' : ''}>
                      <SelectValue placeholder="Selecionar setor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {["Luz e Gas", "Solar", "Carregamento Eletrico", "Telecomunicacoes", "Alarmes", "Outro"].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Origem da Lead *</Label>
                  <Select
                    value={newLeadForm.source}
                    onValueChange={(v) => {
                      if (v === '__create__') { setNewSourceDialogOpen(true); return; }
                      setNewLeadForm(p => ({...p, source: v}));
                    }}
                  >
                    <SelectTrigger data-testid="new-lead-source">
                      <SelectValue placeholder="Selecionar origem..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(leadSourceLabels).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                      {canManageSources && (
                        <SelectItem value="__create__" className="text-[#2D3E6F] font-medium border-t mt-1 pt-2">
                          ➕ Criar nova origem...
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Data 1º Contacto (opcional)</Label>
                  <Input
                    type="datetime-local"
                    value={newLeadForm.next_action_at}
                    onChange={(e) => setNewLeadForm(p => ({...p, next_action_at: e.target.value}))}
                    data-testid="new-lead-next-action"
                  />
                  <p className="text-xs text-slate-400 mt-1">Sistema vai lembrar-te perto da hora.</p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-slate-600">Morada</Label>
                  <Input
                    value={newLeadForm.client_address}
                    onChange={(e) => setNewLeadForm(p => ({...p, client_address: e.target.value}))}
                    placeholder="Morada completa"
                    data-testid="new-lead-address"
                  />
                </div>
                {canAssignToOthers && (
                  <>
                    <div>
                      <Label className="text-slate-600">Parceiro</Label>
                      <Select
                        value={newLeadForm.partner_id || '__none__'}
                        onValueChange={(v) => {
                          const pid = v === '__none__' ? '' : v;
                          // Quando muda parceiro, limpar comercial selecionado se já não pertencer
                          const stillValid = assignableUsers.find(u => u.id === newLeadForm.assigned_to && (!pid || u.partner_id === pid));
                          setNewLeadForm(p => ({ ...p, partner_id: pid, assigned_to: stillValid ? p.assigned_to : '' }));
                        }}
                      >
                        <SelectTrigger data-testid="new-lead-partner">
                          <SelectValue placeholder="Qualquer parceiro" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Qualquer parceiro —</SelectItem>
                          {assignablePartners.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} {typeof p.comerciais_count === 'number' ? `(${p.comerciais_count})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-600">Comercial (opcional)</Label>
                      <Select
                        value={newLeadForm.assigned_to || '__self__'}
                        onValueChange={(v) => setNewLeadForm(p => ({ ...p, assigned_to: v === '__self__' ? '' : v }))}
                      >
                        <SelectTrigger data-testid="new-lead-assigned-to">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__self__">— Atribuir a mim —</SelectItem>
                          {assignableUsers
                            .filter(u => !newLeadForm.partner_id || u.partner_id === newLeadForm.partner_id)
                            .map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name} {u.role === 'chefe_equipa' ? '(Chefe Equipa)' : ''}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {newLeadForm.partner_id && assignableUsers.filter(u => u.partner_id === newLeadForm.partner_id).length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">Sem comerciais activos neste parceiro.</p>
                      )}
                    </div>
                  </>
                )}
                <div className="sm:col-span-2">
                  <Label className="text-slate-600">Observacoes</Label>
                  <Textarea
                    value={newLeadForm.notes}
                    onChange={(e) => setNewLeadForm(p => ({...p, notes: e.target.value}))}
                    placeholder="Notas adicionais..."
                    rows={2}
                    data-testid="new-lead-notes"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewLeadOpen(false)}>Cancelar</Button>
              <Button
                onClick={handleSubmitNewLead}
                disabled={newLeadSubmitting || !newLeadForm.client_name || !newLeadForm.client_phone}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                data-testid="submit-new-lead-btn"
              >
                {newLeadSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Submeter Lead
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Lead Dialog (full fields edit) */}
        <Dialog open={editLeadOpen} onOpenChange={(o) => { if (!o && !editLeadSaving) { setEditLeadOpen(false); setEditLeadForm(null); } }}>
          <DialogContent className="max-w-lg" data-testid="edit-lead-full-dialog">
            <DialogHeader>
              <DialogTitle>Editar Lead</DialogTitle>
            </DialogHeader>
            {editLeadForm && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-slate-600">Nome do Cliente *</Label>
                    <Input
                      value={editLeadForm.client_name}
                      onChange={(e) => setEditLeadForm(p => ({ ...p, client_name: e.target.value }))}
                      data-testid="edit-lead-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600">Telefone *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={editLeadForm.client_phone_tipo || 'telemovel'}
                        onValueChange={(v) => setEditLeadForm(p => ({ ...p, client_phone_tipo: v }))}
                      >
                        <SelectTrigger className="w-28" data-testid="edit-lead-phone-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="telemovel">Telemóvel</SelectItem>
                          <SelectItem value="fixo">Fixo</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={editLeadForm.client_phone}
                        onChange={(e) => setEditLeadForm(p => ({ ...p, client_phone: e.target.value }))}
                        maxLength={9}
                        data-testid="edit-lead-phone"
                      />
                    </div>
                    {editLeadForm.client_phone && !validatePhone(editLeadForm.client_phone, editLeadForm.client_phone_tipo || 'telemovel').ok && (
                      <p className="text-xs text-rose-600 mt-1">
                        {validatePhone(editLeadForm.client_phone, editLeadForm.client_phone_tipo || 'telemovel').error}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-600">Email</Label>
                    <Input
                      type="email"
                      value={editLeadForm.client_email}
                      onChange={(e) => setEditLeadForm(p => ({ ...p, client_email: e.target.value }))}
                      placeholder="cliente@email.com"
                      data-testid="edit-lead-email"
                    />
                    {editLeadForm.client_email && !validateEmailFmt(editLeadForm.client_email) && (
                      <p className="text-xs text-rose-600 mt-1" data-testid="edit-lead-email-error">
                        Email inválido. Usa o formato nome@dominio.xx
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-600">NIF</Label>
                    <Input
                      value={editLeadForm.client_nif}
                      onChange={(e) => setEditLeadForm(p => ({ ...p, client_nif: e.target.value }))}
                      maxLength={9}
                      data-testid="edit-lead-nif"
                    />
                    {editLeadForm.client_nif && !validateNIF(editLeadForm.client_nif).ok && (
                      <p className="text-xs text-rose-600 mt-1">{validateNIF(editLeadForm.client_nif).error}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-600">Localidade</Label>
                    <Input
                      value={editLeadForm.client_locality}
                      onChange={(e) => setEditLeadForm(p => ({ ...p, client_locality: e.target.value }))}
                      data-testid="edit-lead-locality"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600">Origem da Lead</Label>
                    <Select
                      value={editLeadForm.source || ''}
                      onValueChange={(v) => {
                        if (v === '__create__') { setNewSourceDialogOpen(true); return; }
                        setEditLeadForm(p => ({ ...p, source: v }));
                      }}
                    >
                      <SelectTrigger data-testid="edit-lead-source">
                        <SelectValue placeholder="Selecionar origem..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(leadSourceLabels).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                        {canManageSources && (
                          <SelectItem value="__create__" className="text-[#2D3E6F] font-medium border-t mt-1 pt-2">
                            ➕ Criar nova origem...
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600">Servico Pretendido</Label>
                    <Select
                      value={editLeadForm.service_interest || '__none__'}
                      onValueChange={(v) => setEditLeadForm(p => ({ ...p, service_interest: v === '__none__' ? '' : v }))}
                    >
                      <SelectTrigger data-testid="edit-lead-service"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {["Luz e Gas", "Solar", "Carregamento Eletrico", "Telecomunicacoes", "Alarmes", "Outro"].map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-slate-600">Morada</Label>
                    <Input
                      value={editLeadForm.client_address}
                      onChange={(e) => setEditLeadForm(p => ({ ...p, client_address: e.target.value }))}
                      data-testid="edit-lead-address"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-slate-600">Observacoes</Label>
                    <Textarea
                      value={editLeadForm.notes}
                      onChange={(e) => setEditLeadForm(p => ({ ...p, notes: e.target.value }))}
                      rows={2}
                      data-testid="edit-lead-notes"
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditLeadOpen(false); setEditLeadForm(null); }} disabled={editLeadSaving}>Cancelar</Button>
              <Button
                onClick={handleSaveEditLead}
                disabled={editLeadSaving || !editLeadForm?.client_name || !editLeadForm?.client_phone}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                data-testid="save-edit-lead-btn"
              >
                {editLeadSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Lead confirmation */}
        <AlertDialog open={!!deleteLeadConfirm} onOpenChange={(o) => { if (!o && !deleting) setDeleteLeadConfirm(null); }}>
          <AlertDialogContent data-testid="delete-lead-dialog">
            <AlertDialogHeader>
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-red-100 flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <AlertDialogTitle className="text-red-700">Atenção — eliminar lead?</AlertDialogTitle>
                  <AlertDialogDescription className="mt-2 text-slate-600">
                    Vais eliminar definitivamente a lead de{' '}
                    <span className="font-semibold text-slate-900">&ldquo;{deleteLeadConfirm?.client_name}&rdquo;</span>{' '}
                    {deleteLeadConfirm?.client_phone ? `(${deleteLeadConfirm.client_phone})` : ''}.
                    <br /><br />
                    Esta ação <strong>não pode ser desfeita</strong>.
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-4">
              <AlertDialogCancel disabled={deleting} data-testid="delete-lead-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteLead}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                data-testid="delete-lead-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Sim, eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* New Source Dialog */}
        <Dialog open={newSourceDialogOpen} onOpenChange={(o) => { if (!o && !creatingSource) { setNewSourceDialogOpen(false); setNewSourceLabelInput(''); } }}>
          <DialogContent className="max-w-sm" data-testid="new-source-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">➕ Nova Origem de Lead</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-slate-600">Nome da origem *</Label>
                <Input
                  value={newSourceLabelInput}
                  onChange={(e) => setNewSourceLabelInput(e.target.value)}
                  placeholder="Ex: TikTok Ads, Stand FNAC, Rádio..."
                  maxLength={60}
                  data-testid="new-source-label"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !creatingSource) { e.preventDefault(); handleCreateNewSource(); } }}
                />
                <p className="text-[11px] text-slate-400 mt-1">Inclui emoji no início se quiseres (ex: 🎯 TikTok Ads)</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setNewSourceDialogOpen(false); setNewSourceLabelInput(''); }} disabled={creatingSource}>Cancelar</Button>
              <Button
                onClick={handleCreateNewSource}
                disabled={creatingSource || !newSourceLabelInput.trim()}
                className="bg-[#2D3E6F] hover:bg-[#1a2a4a]"
                data-testid="submit-new-source"
              >
                {creatingSource ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Criar Origem
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <BulkAssignDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          leadIds={dialogLeadIds}
          allowUnassign={dialogLeadIds.length > 0 && dialogLeadIds.every((id) => filteredLeads.find((l) => l.id === id)?.assigned_to)}
          onDone={() => { clearSelection(); setDialogLeadIds([]); fetchLeads(); }}
        />

        {/* Confirmar apagar em massa */}
        <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
          <DialogContent className="max-w-md" data-testid="bulk-delete-dialog">
            <DialogHeader>
              <DialogTitle>Apagar leads selecionadas</DialogTitle>
              <DialogDescription>
                Vais apagar <strong>{selectedIds.size}</strong> lead(s) e todo o seu histórico de contactos. Leads convertidas (com contrato) ficam protegidas. Esta ação é irreversível.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} data-testid="bulk-delete-cancel-btn">Cancelar</Button>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doBulkDelete} disabled={deletingBulk} data-testid="bulk-delete-confirm-btn">
                {deletingBulk ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Apagar {selectedIds.size} lead(s)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Classificar Setor em massa */}
        <Dialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="sector-dialog">
            <DialogHeader>
              <DialogTitle>Classificar Setor das Leads</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-end gap-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500 mb-1 block">Aplicar a TODAS as {leadsWithoutSector.length} leads sem setor</Label>
                  <Select value={bulkSectorValue} onValueChange={setBulkSectorValue}>
                    <SelectTrigger data-testid="bulk-sector-select"><SelectValue placeholder="Selecionar setor..." /></SelectTrigger>
                    <SelectContent>
                      {SECTOR_OPTIONS.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="bg-[#2D3E6F] hover:bg-[#1a2a4a] h-9"
                  disabled={!bulkSectorValue || settingSector || leadsWithoutSector.length === 0}
                  onClick={async () => { await applyBulkSector(leadsWithoutSector.map(l => l.id), bulkSectorValue); setSectorDialogOpen(false); }}
                  data-testid="apply-all-sector-btn"
                >Aplicar a todas</Button>
              </div>
              <p className="text-xs text-slate-400">Ou classifica individualmente abaixo:</p>
              <div className="max-h-[45vh] overflow-y-auto space-y-1.5">
                {leadsWithoutSector.map((l) => (
                  <div key={l.id} className="flex items-center gap-2 border border-slate-100 rounded-lg p-2" data-testid={`sector-row-${l.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{l.client_name}</p>
                      <p className="text-xs text-slate-400 font-mono">{l.client_phone || '—'}</p>
                    </div>
                    <Select onValueChange={(v) => setOneSector(l.id, v)}>
                      <SelectTrigger className="w-[160px] h-8" data-testid={`row-sector-select-${l.id}`}><SelectValue placeholder="Definir setor" /></SelectTrigger>
                      <SelectContent>
                        {SECTOR_OPTIONS.map(s => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {leadsWithoutSector.length === 0 && (
                  <p className="text-center text-sm text-emerald-600 py-6">✓ Todas as leads têm setor definido!</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
